
import React, { useState, useEffect, useRef } from 'react';
import { Button } from '@/components/ui/button';
import { Radio, Volume2, Volume1, VolumeX } from 'lucide-react';
import Hls from 'hls.js';
import { cn } from '@/lib/utils';

export const BloombergRadioButton = () => {
  const [isPlaying, setIsPlaying] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [volume, setVolume] = useState(0.5);
  const audioRef = useRef<HTMLAudioElement | null>(null);
  const hlsRef = useRef<Hls | null>(null);

  // Initialize audio on component mount
  useEffect(() => {
    const audio = new Audio();
    audioRef.current = audio;
    audio.volume = volume;

    // Cleanup on unmount
    return () => {
      if (hlsRef.current) {
        hlsRef.current.destroy();
      }
      if (audioRef.current) {
        audioRef.current.pause();
        audioRef.current.src = '';
      }
    };
  }, []);

  const togglePlay = () => {
    if (!audioRef.current) return;

    if (isPlaying) {
      audioRef.current.pause();
      setIsPlaying(false);
    } else {
      setIsLoading(true);
      const bloombergRadioUrl = "https://www.bloomberg.com/media-manifest/streams/us.m3u8";

      // Check if the browser supports HLS natively
      if (audioRef.current.canPlayType('application/vnd.apple.mpegurl')) {
        // Native support exists - typically iOS Safari
        audioRef.current.src = bloombergRadioUrl;
        audioRef.current.play().catch(e => console.error("Playback error:", e));
      } else if (Hls.isSupported()) {
        // Use HLS.js for browsers that don't have native support
        if (hlsRef.current) {
          hlsRef.current.destroy();
        }
        
        const hls = new Hls();
        hlsRef.current = hls;
        hls.loadSource(bloombergRadioUrl);
        hls.attachMedia(audioRef.current);
        hls.on(Hls.Events.MANIFEST_PARSED, () => {
          audioRef.current?.play().catch(e => console.error("Playback error:", e));
        });
      } else {
        console.error('Neither native HLS support nor HLS.js is available');
        setIsLoading(false);
        return;
      }

      audioRef.current.onplaying = () => {
        setIsPlaying(true);
        setIsLoading(false);
      };

      audioRef.current.onerror = () => {
        setIsLoading(false);
        setIsPlaying(false);
        console.error("Audio playback error");
      };
    }
  };

  const toggleVolume = () => {
    if (!audioRef.current) return;
    
    const newVolume = volume === 0 ? 0.5 : 0;
    setVolume(newVolume);
    audioRef.current.volume = newVolume;
  };

  // Determine which volume icon to show
  const VolumeIcon = volume === 0 ? VolumeX : volume < 0.5 ? Volume1 : Volume2;

  return (
    <div className="fixed bottom-6 right-6 z-50 flex items-center gap-2">
      <Button
        onClick={toggleVolume}
        size="icon"
        variant="outline"
        className={cn(
          "rounded-full shadow-md bg-white/70 backdrop-blur-sm hover:bg-white/90",
          "border border-gray-200 transition-all duration-300",
          volume === 0 && "opacity-50"
        )}
      >
        <VolumeIcon className="h-4 w-4 text-gray-700" />
      </Button>
      
      <Button
        onClick={togglePlay}
        className={cn(
          "flex items-center gap-2 px-4 rounded-full shadow-md",
          "bg-white/70 backdrop-blur-sm hover:bg-white/90 text-gray-700",
          "border border-gray-200 transition-all duration-300",
          isPlaying && "bg-primary/10 hover:bg-primary/20 border-primary/20",
          isLoading && "animate-pulse"
        )}
      >
        <Radio className={cn(
          "h-4 w-4 transition-all",
          isPlaying && "text-primary animate-pulse-slow"
        )} />
        {isLoading ? (
          <span>Loading...</span>
        ) : (
          <span>{isPlaying ? "Bloomberg Radio Live" : "Play Bloomberg Radio"}</span>
        )}
      </Button>
    </div>
  );
};
